
import purchasesServices from '@/services/purchases.services.js';
import messages from '@/constants/messages.js';
import mixin from "@/mixin/index.js";
import router from '@/router/index.js';
import { displayUpsellScreen, rcConfigureOrLogin, rcManageSubscriptions, initializeStripe } from "@/plugins/purchases.plugins.js";
import { isChromeExtension } from '@/platform/runtime.js';
import { openExternalUrl } from '@/platform/browser.js';

const { _getMessage, _getLabel, _isCordovaApp, _getPlatform } = mixin.methods;
const root = { root: true };

export default {

    configurePurchases: () => {
        rcConfigureOrLogin();
    },

    createCheckoutSession: async ({ dispatch, commit, rootGetters }, product) => {
        try {
            if (!product) {
                const offering = await getOfferingsForUser({ commit, dispatch, rootGetters });
                product = offering.stripeOffering.products[0];
            }
            let { price } = product;
            commit('general/setLoading', true, root);
            if (rootGetters['general/isPremium']) {
                alreadyProUserMessage(commit, rootGetters);
                return dispatch('general/routeRedirect', { name: 'update' }, root);
            }
            const sessionBody = {
                cupom: router.app._route.query.cupom || null,
                price: price || router.app._route.query.price || process.env.VUE_APP_STRIPE_MONTHLY_PRICE,
                success_url: process.env.VUE_APP_STRIPE_SUCCESS_URL,
                cancel_url: process.env.VUE_APP_STRIPE_CANCEL_URL
            };
            const { checkoutSessionId, checkoutUrl } = await purchasesServices.createCheckoutSession(sessionBody);
            if (isChromeExtension()) {
                if (!checkoutUrl) throw new Error('Checkout URL unavailable');
                commit('general/setLoading', false, root);
                return openExternalUrl(checkoutUrl);
            }
            const stripe = await initializeStripe();
            return stripe.redirectToCheckout({ sessionId: checkoutSessionId });
        } catch (err) {
            commit('general/setLoading', false, root);
            dispatch("general/showError", messages.PURCHASE_ERROR, root);
            throw err;
        }
    },

    openPurchasesFromMenu: async ({ rootGetters, state, commit, dispatch }) => {
        if (!rootGetters['general/toggles'] || !rootGetters['general/toggles']['purchases']) {
            return commit('general/setConfirmModel', {
                active: true,
                alert: true,
                text: messages.FEATURE_TOGGLE_OFF,
                callback: (_) => { },
                confirmText: _getLabel("CONTINUE"),
            }, root);
        }

        if (process.env.VUE_APP_ENV === "partners") {
            return alert('Este item ainda não está disponível para parceiros. Acesse nosso portal https://app.vetalfa.com para ter acesso a este recurso.')
        }
        dispatch("general/logEvent", 'openPurchasesFromMenu', root);

        if (!_isCordovaApp()) {
            state.openPurchasesFromMenu = true;
        } else {
            const offering = await getOfferingsForUser({ commit, dispatch, rootGetters });
            displayUpsellScreen({ openPurchasesFromMenu: true, offering: offering });
        }
    },

    openPurchases: async ({ rootGetters, commit, dispatch }, delay = null) => {

        if (!rootGetters['general/toggles'] || !rootGetters['general/toggles']['purchases']) {
            return commit('general/setConfirmModel', {
                alert: true,
                text: messages.FEATURE_TOGGLE_OFF,
                callback: (_) => { },
                confirmText: _getLabel("CONTINUE"),
            }, root);
        }

        if (delay) {
            await new Promise(resolve => setTimeout(resolve, delay));
        }
        const user = rootGetters["general/user"];
        if (process.env.VUE_APP_ENV === "partners") {
            return alert('Este item ainda não está disponível para parceiros. Acesse nosso portal https://app.vetalfa.com para ter acesso a este recurso.')
        }
        if (!user || !!user.isPremium) {
            return;
        }
        dispatch("general/logEvent", 'openPurchases', root);
        const offering = await getOfferingsForUser({ commit, dispatch, rootGetters });
        if (!_isCordovaApp()) {
            displayPaywallStripe({ commit, dispatch, rootGetters }, offering);
        } else {
            displayUpsellScreen({ openPurchasesFromMenu: false, offering: offering });
        }
    },

    purchasesManageSubscriptions: async ({ commit, dispatch, rootGetters }) => {
        try {
            await dispatch("general/refreshAccessToken", null, root);

            if (rootGetters['general/user'].store !== _getPlatform()) {
                return alreadyProUserMessage(commit, rootGetters);
            }
            if (_isCordovaApp()) {
                return rcManageSubscriptions();
            }
            commit("general/setLoadingOn", true, root);
            const { session } = await purchasesServices.createCustomersPortalLink(process.env.VUE_APP_STRIPE_CANCEL_URL);
            if (isChromeExtension()) {
                openExternalUrl(session.url);
            } else {
                window.location.assign(session.url);
            }
        } catch (err) {
            dispatch("general/showError", messages.PURCHASE_ERROR, root);
            throw err;
        } finally {
            commit("general/setLoadingOff", false, root);
        }
    },

}


async function getOfferingsForUser({ commit, dispatch, rootGetters }) {
    try {
        commit('general/setLoading', true, root);
        const offerings = await purchasesServices.getOfferingsForUser();
        return offerings;
    } catch {
        return {
            revenueCatOffering: process.env.VUE_APP_REVENUE_CAT_VERSION_OFFERING,
            stripeOffering: {
                metadata: JSON.parse(process.env.VUE_APP_STRIPE_METADATA),
                products: [
                    {
                        product: {
                            priceString: process.env.VUE_APP_STRIPE_PRICE_STRING
                        },
                        price: process.env.VUE_APP_STRIPE_MONTHLY_PRICE
                    }
                ]
            }
        }
    } finally {
        commit('general/setLoading', false, root);
    }
}

async function displayPaywallStripe({ commit, dispatch, rootGetters }, offering) {
    commit('setMetadata', offering.stripeOffering.metadata);
    commit('setProducts', offering.stripeOffering.products);
    commit('setPurchasesModel', true);
}

function alreadyProUserMessage(commit, rootGetters) {
    return commit('general/setConfirmModel', {
        active: true,
        alert: true,
        text: _getMessage('MANAGE_SUBSCRIPTION_STORE_ERROR').replace('<platform>', {
            'website': "Site",
            "ios": "Apple Store",
            "android": "Google Play Store"
        }[rootGetters['general/user'].store] || "sua assinatura é gerenciada por uma organização externa"),
        callback: (_) => { },
        confirmText: _getLabel("CONTINUE"),
    }, root);
}
